import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

interface CaseEvidenceAttachment {
  id: string;
  customerId: string;
  caseId: string | null;
  incidentId: string | null;
  incidentNumber: string | null;
  category: string;
  visibility: string;
  customerVisible?: boolean;
  description: string | null;
  originalFilename: string;
  contentType: string;
  fileSizeBytes: number;
  sha256Hash: string;
  uploadedByEmail: string | null;
  uploadedByName?: string | null;
  uploadedAtUtc: string;
  scanStatus?: string | null;
  scanResult?: string | null;
  scannedAtUtc?: string | null;
  isDeleted: boolean;
  deletedBy?: string | null;
  deletedAtUtc?: string | null;
  retentionUntilUtc?: string | null;
  isLocked?: boolean;
  lockedBy?: string | null;
  lockedAtUtc?: string | null;
  lockReason?: string | null;
  legalHold?: boolean;
  legalHoldReason?: string | null;
  legalHoldBy?: string | null;
  legalHoldAtUtc?: string | null;
}

interface CaseEvidenceExportPack {
  id: string;
  customerId: string;
  caseId: string | null;
  incidentId: string | null;
  incidentNumber: string | null;
  title: string;
  originalFilename: string;
  fileSizeBytes: number;
  sha256Hash: string;
  attachmentCount: number;
  includedAttachmentIds: string[];
  customerVisible: boolean;
  createdByEmail: string | null;
  createdByName: string | null;
  createdAtUtc: string;
  retentionUntilUtc?: string | null;
  status: string;
}

interface CaseEvidenceAuditEvent {
  id: string;
  attachmentId: string;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAtUtc: string;
}

interface ListResponse {
  ok: boolean;
  attachments: CaseEvidenceAttachment[];
  error?: string;
}

interface UploadResponse {
  ok: boolean;
  attachment: CaseEvidenceAttachment;
  error?: string;
}

interface ConfigResponse {
  ok: boolean;
  categories?: string[];
  storage?: {
    maxBytes?: number;
    lifecycle?: {
      defaultRetentionDays?: number | null;
      deletedRetentionDays?: number | null;
      legalHoldRetentionDays?: number | null;
      exportPackRetentionDays?: number | null;
      malwareScanEnabled?: boolean;
      azureImmutabilityEnabled?: boolean;
    };
  };
  lifecycle?: {
    defaultRetentionDays?: number | null;
    deletedRetentionDays?: number | null;
    legalHoldRetentionDays?: number | null;
    exportPackRetentionDays?: number | null;
    malwareScanEnabled?: boolean;
    azureImmutabilityEnabled?: boolean;
  };
}

interface AuditResponse {
  ok: boolean;
  events: CaseEvidenceAuditEvent[];
}

interface ExportPacksResponse {
  ok: boolean;
  exportPacks: CaseEvidenceExportPack[];
}

interface CaseEvidencePanelProps {
  customerId?: string;
  incidentId?: string;
  incidentNumber?: string;
  caseId?: string;
  incident?: Record<string, unknown>;
  armIncidentName?: string;
  graphIncidentId?: string;
  providerName?: string;
  title?: string;
}

const DEFAULT_CATEGORIES = ['Screenshot', 'Report', 'Log export', 'Customer evidence', 'RCA document', 'Other'];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function compactHash(hash: string): string {
  if (!hash) return '';
  return `${hash.slice(0, 12)}…${hash.slice(-8)}`;
}

function scanLabel(status?: string | null): string {
  const s = String(status || 'not_configured').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function scanClass(status?: string | null): string {
  switch (String(status || '').toLowerCase()) {
    case 'clean': return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300';
    case 'malicious': return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300';
    case 'failed': return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300';
    case 'pending': return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300';
    default: return 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-300';
  }
}

function actorName(row: CaseEvidenceAttachment): string {
  return row.uploadedByName || row.uploadedByEmail || 'Unknown user';
}

function packActorName(row: CaseEvidenceExportPack): string {
  return row.createdByName || row.createdByEmail || 'Unknown user';
}

function promptText(message: string, fallback = ''): string | null {
  const value = window.prompt(message, fallback);
  if (value === null) return null;
  return value.trim();
}

function promptRetention(): string | null {
  const value = window.prompt('Optional retention-until UTC date/time. Leave blank to use the configured default. Example: 2027-05-25T00:00:00Z', '');
  if (value === null) return null;
  return value.trim();
}

export function CaseEvidencePanel({ customerId, incidentId, incidentNumber, caseId, incident, armIncidentName, graphIncidentId, providerName, title }: CaseEvidencePanelProps) {
  const { canCapability } = useAuth();
  const canRead = canCapability('case_evidence', 'read');
  const canUpload = canCapability('case_evidence', 'upload');
  const canDownload = canCapability('case_evidence', 'download');
  const canDelete = canCapability('case_evidence', 'delete');
  const canRestore = canCapability('case_evidence', 'restore');
  const canViewDeleted = canCapability('case_evidence', 'view_deleted');
  const canViewHash = canCapability('case_evidence', 'view_hash');
  const canViewScan = canCapability('case_evidence', 'view_scan_result');
  const canViewAudit = canCapability('case_evidence', 'view_audit');
  const canLock = canCapability('case_evidence', 'lock_evidence');
  const canUnlock = canCapability('case_evidence', 'unlock_evidence');
  const canLegalHold = canCapability('case_evidence', 'manage_legal_hold');
  const canCustomerVisible = canCapability('case_evidence', 'mark_customer_visible');
  const canCreatePack = canCapability('case_evidence', 'create_export_pack');
  const canDownloadPack = canCapability('case_evidence', 'download_export_pack');
  const canGenerateAutoPack = canCapability('case_evidence', 'generate_auto_pack');

  const [attachments, setAttachments] = useState<CaseEvidenceAttachment[]>([]);
  const [exportPacks, setExportPacks] = useState<CaseEvidenceExportPack[]>([]);
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [lifecycleText, setLifecycleText] = useState<string>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [packLoading, setPackLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [autoPackLoading, setAutoPackLoading] = useState(false);
  const [autoPackMessage, setAutoPackMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [category, setCategory] = useState('Screenshot');
  const [description, setDescription] = useState('');
  const [customerVisibleOnUpload, setCustomerVisibleOnUpload] = useState(false);
  const [auditOpen, setAuditOpen] = useState<Record<string, boolean>>({});
  const [auditRows, setAuditRows] = useState<Record<string, CaseEvidenceAuditEvent[]>>({});
  const [auditLoading, setAuditLoading] = useState<Record<string, boolean>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (incidentId) params.set('incidentId', incidentId);
    if (caseId) params.set('caseId', caseId);
    if (includeDeleted && canViewDeleted) params.set('includeDeleted', 'true');
    params.set('limit', '100');
    return params.toString();
  }, [customerId, incidentId, caseId, includeDeleted, canViewDeleted]);

  const loadConfig = useCallback(async () => {
    if (!canRead) return;
    try {
      const data = await apiFetch<ConfigResponse>('/case-evidence/config');
      const nextCategories = Array.isArray(data.categories) && data.categories.length > 0 ? data.categories : DEFAULT_CATEGORIES;
      setCategories(nextCategories);
      if (!nextCategories.includes(category)) setCategory(nextCategories[0] || 'Other');
      const lifecycle = data.lifecycle || data.storage?.lifecycle;
      const bits: string[] = [];
      if (typeof lifecycle?.defaultRetentionDays === 'number') bits.push(`default retention ${lifecycle.defaultRetentionDays}d`);
      if (typeof lifecycle?.deletedRetentionDays === 'number') bits.push(`deleted retention ${lifecycle.deletedRetentionDays}d`);
      if (typeof lifecycle?.legalHoldRetentionDays === 'number') bits.push(`legal hold ${lifecycle.legalHoldRetentionDays}d`);
      if (typeof lifecycle?.exportPackRetentionDays === 'number') bits.push(`export pack ${lifecycle.exportPackRetentionDays}d`);
      if (lifecycle?.malwareScanEnabled) bits.push('malware scan tracking enabled');
      if (lifecycle?.azureImmutabilityEnabled) bits.push('Azure immutability opt-in enabled');
      setLifecycleText(bits.join(' · '));
    } catch {
      // Non-blocking; backend still enforces storage policy.
    }
  }, [canRead, category]);

  const load = useCallback(async () => {
    if (!customerId || !canRead) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<ListResponse>(`/case-evidence?${qs}`);
      setAttachments(data.attachments ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [customerId, canRead, qs]);

  const loadExportPacks = useCallback(async () => {
    if (!customerId || !canRead) return;
    setPackLoading(true);
    try {
      const data = await apiFetch<ExportPacksResponse>(`/case-evidence/export-packs?${qs}`);
      setExportPacks(data.exportPacks ?? []);
    } catch {
      // Keep attachments usable even if export pack list fails.
    } finally {
      setPackLoading(false);
    }
  }, [customerId, canRead, qs]);

  useEffect(() => { void loadConfig(); }, [loadConfig]);
  useEffect(() => { void load(); void loadExportPacks(); }, [load, loadExportPacks]);

  function replaceAttachment(next: CaseEvidenceAttachment) {
    setAttachments(prev => prev.map(a => a.id === next.id ? next : a));
  }

  function replacePack(next: CaseEvidenceExportPack) {
    setExportPacks(prev => prev.map(a => a.id === next.id ? next : a));
  }

  async function upload() {
    if (!customerId || !selectedFile) return;
    if (!canUpload) {
      setError('Your role is not allowed to upload case evidence. Enable Case Evidence / Upload Evidence in Permissions Master.');
      return;
    }
    if (customerVisibleOnUpload && !canCustomerVisible) {
      setError('Your role is not allowed to mark evidence as customer-visible.');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('file', selectedFile);
      form.set('category', category);
      form.set('visibility', customerVisibleOnUpload ? 'customer_visible' : 'internal');
      form.set('customerVisible', customerVisibleOnUpload ? 'true' : 'false');
      form.set('description', description);
      if (incidentId) form.set('incidentId', incidentId);
      if (incidentNumber) form.set('incidentNumber', incidentNumber);
      if (caseId) form.set('caseId', caseId);

      const data = await apiFetch<UploadResponse>(`/case-evidence?customerId=${encodeURIComponent(customerId)}`, { method: 'POST', body: form });
      setAttachments(prev => [data.attachment, ...prev]);
      setSelectedFile(null);
      setDescription('');
      setCustomerVisibleOnUpload(false);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  function download(row: CaseEvidenceAttachment) {
    if (!customerId) return;
    if (!canDownload) {
      setError('Your role is not allowed to download case evidence. Enable Case Evidence / Download Evidence in Permissions Master.');
      return;
    }
    const url = `${getApiBase()}/case-evidence/${encodeURIComponent(row.id)}/download?customerId=${encodeURIComponent(customerId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function downloadPack(row: CaseEvidenceExportPack) {
    if (!customerId || !canDownloadPack) return;
    const url = `${getApiBase()}/case-evidence/export-packs/${encodeURIComponent(row.id)}/download?customerId=${encodeURIComponent(customerId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function deleteAttachment(row: CaseEvidenceAttachment) {
    if (!customerId || !canDelete) return;
    if (row.isLocked || row.legalHold) {
      setError('Locked or legal-hold evidence cannot be deleted. Remove legal hold / unlock first.');
      return;
    }
    const ok = window.confirm(`Soft-delete evidence attachment "${row.originalFilename}"? It can be restored by users with Restore Deleted Evidence permission.`);
    if (!ok) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' });
      if (includeDeleted && canViewDeleted) replaceAttachment(data.attachment);
      else setAttachments(prev => prev.filter(a => a.id !== row.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function restoreAttachment(row: CaseEvidenceAttachment) {
    if (!customerId || !canRestore) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/restore?customerId=${encodeURIComponent(customerId)}`, { method: 'POST' });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function refreshScan(row: CaseEvidenceAttachment) {
    if (!customerId || !canViewScan) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/scan-status/refresh?customerId=${encodeURIComponent(customerId)}`, { method: 'POST' });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleAudit(row: CaseEvidenceAttachment) {
    const nextOpen = !auditOpen[row.id];
    setAuditOpen(prev => ({ ...prev, [row.id]: nextOpen }));
    if (!nextOpen || auditRows[row.id] || !customerId || !canViewAudit) return;
    setAuditLoading(prev => ({ ...prev, [row.id]: true }));
    setError(null);
    try {
      const data = await apiFetch<AuditResponse>(`/case-evidence/${encodeURIComponent(row.id)}/audit?customerId=${encodeURIComponent(customerId)}&limit=50`);
      setAuditRows(prev => ({ ...prev, [row.id]: data.events ?? [] }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAuditLoading(prev => ({ ...prev, [row.id]: false }));
    }
  }

  async function setCustomerVisible(row: CaseEvidenceAttachment, customerVisible: boolean) {
    if (!customerId || !canCustomerVisible) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/customer-visible?customerId=${encodeURIComponent(customerId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerVisible }),
      });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function lockAttachment(row: CaseEvidenceAttachment) {
    if (!customerId || !canLock) return;
    const reason = promptText('Reason for locking this evidence:', row.lockReason || 'Evidence finalized for case review');
    if (reason === null) return;
    const retentionUntilUtc = promptRetention();
    if (retentionUntilUtc === null) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/lock?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, retentionUntilUtc: retentionUntilUtc || null }),
      });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function unlockAttachment(row: CaseEvidenceAttachment) {
    if (!customerId || !canUnlock) return;
    const reason = promptText('Reason for unlocking this evidence:', 'Administrative unlock');
    if (reason === null) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/unlock?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function setLegalHold(row: CaseEvidenceAttachment, enabled: boolean) {
    if (!customerId || !canLegalHold) return;
    const reason = promptText(enabled ? 'Reason for legal hold:' : 'Reason for removing legal hold:', enabled ? 'Legal / evidence preservation hold' : 'Legal hold released');
    if (reason === null) return;
    const retentionUntilUtc = enabled ? promptRetention() : '';
    if (retentionUntilUtc === null) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; attachment: CaseEvidenceAttachment }>(`/case-evidence/${encodeURIComponent(row.id)}/legal-hold?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, reason, retentionUntilUtc: retentionUntilUtc || null }),
      });
      replaceAttachment(data.attachment);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function createExportPack() {
    if (!customerId || !canCreatePack) return;
    const title = promptText('Export pack title:', `Case Evidence Pack ${incidentNumber ? `#${incidentNumber}` : ''}`.trim());
    if (title === null) return;
    const customerVisible = canCustomerVisible && window.confirm('Mark this export pack as customer-visible?');
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; exportPack: CaseEvidenceExportPack }>(`/case-evidence/export-packs?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incidentId: incidentId || null, incidentNumber: incidentNumber || null, caseId: caseId || null, title, customerVisible }),
      });
      setExportPacks(prev => [data.exportPack, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function createAutoPack() {
    if (!customerId || !canGenerateAutoPack) return;
    const defaultTitle = `Evidence-to-Case Auto-Pack ${incidentNumber ? `#${incidentNumber}` : ''}`.trim();
    const packTitle = promptText('Evidence-to-Case auto-pack title:', title || defaultTitle);
    if (packTitle === null) return;
    const customerVisible = canCustomerVisible && window.confirm('Mark this auto-pack as customer-visible?');
    setAutoPackLoading(true);
    setAutoPackMessage(null);
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; exportPack: CaseEvidenceExportPack; warnings?: string[] }>(`/case-evidence/auto-pack?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          incidentId: incidentId || null,
          incidentNumber: incidentNumber || null,
          caseId: caseId || null,
          title: packTitle || defaultTitle || 'Evidence-to-Case Auto-Pack',
          customerVisible,
          incident: incident || null,
          armIncidentName: armIncidentName || null,
          graphIncidentId: graphIncidentId || null,
          providerName: providerName || null,
        }),
      });
      setExportPacks(prev => [data.exportPack, ...prev]);
      const warningCount = data.warnings?.length ?? 0;
      setAutoPackMessage(warningCount > 0
        ? `Auto-pack created with ${warningCount} warning(s). Download the pack and review generated/generation-warnings.json.`
        : 'Evidence-to-Case auto-pack created successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAutoPackLoading(false);
    }
  }

  async function setPackCustomerVisible(row: CaseEvidenceExportPack, customerVisible: boolean) {
    if (!customerId || !canCustomerVisible) return;
    setError(null);
    try {
      const data = await apiFetch<{ ok: boolean; exportPack: CaseEvidenceExportPack }>(`/case-evidence/export-packs/${encodeURIComponent(row.id)}/customer-visible?customerId=${encodeURIComponent(customerId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerVisible }),
      });
      replacePack(data.exportPack);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!canRead) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <p className="text-xs font-semibold text-muted-foreground">Case Evidence Attachments</p>
          <p className="text-[11px] text-muted-foreground">Upload evidence, lock finalized files, apply legal-hold style retention, generate Evidence-to-Case auto-packs, and mark approved artifacts as customer-visible.</p>
          {lifecycleText && <p className="mt-1 text-[11px] text-cyan-700 dark:text-cyan-300">Lifecycle: {lifecycleText}</p>}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canGenerateAutoPack && <button type="button" onClick={() => void createAutoPack()} disabled={!customerId || autoPackLoading} className="text-xs font-semibold rounded-lg border border-cyan-300 text-cyan-700 dark:text-cyan-300 px-2.5 py-1.5 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 disabled:opacity-50">{autoPackLoading ? 'Generating auto-pack…' : 'Evidence-to-Case Auto-Pack'}</button>}
          {canCreatePack && <button type="button" onClick={() => void createExportPack()} disabled={!customerId || loading} className="text-xs font-semibold rounded-lg border border-emerald-300 text-emerald-700 dark:text-emerald-300 px-2.5 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50">Create export pack</button>}
          {canViewDeleted && (
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
              Show deleted
            </label>
          )}
          <button type="button" onClick={() => { void load(); void loadExportPacks(); }} disabled={loading || !customerId} className="text-xs font-semibold rounded-lg border border-border px-2.5 py-1.5 hover:bg-muted disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {!customerId && <p className="text-xs text-amber-700 dark:text-amber-300">Select a customer before uploading case evidence.</p>}

      {customerId && canUpload && (
        <div className="grid gap-2 mb-3">
          <div className="grid sm:grid-cols-[1fr_170px] gap-2">
            <input ref={fileRef} type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)} className="text-xs rounded-lg border border-border bg-background px-2 py-2" />
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs rounded-lg border border-border bg-background px-2 py-2">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-2">
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="text-xs rounded-lg border border-border bg-background px-2 py-2" placeholder="Optional evidence description / note" />
            <button type="button" onClick={() => void upload()} disabled={!selectedFile || uploading} className="text-xs font-semibold rounded-lg bg-primary text-primary-foreground px-3 py-2 hover:bg-primary/90 disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload Evidence'}
            </button>
          </div>
          {canCustomerVisible && (
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <input type="checkbox" checked={customerVisibleOnUpload} onChange={(e) => setCustomerVisibleOnUpload(e.target.checked)} />
              Mark this upload as customer-visible artifact
            </label>
          )}
          {selectedFile && <p className="text-[11px] text-muted-foreground">Selected: {selectedFile.name} · {formatBytes(selectedFile.size)}</p>}
        </div>
      )}

      {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      {autoPackMessage && <p className="mb-2 text-xs text-cyan-700 dark:text-cyan-300">{autoPackMessage}</p>}

      {exportPacks.length > 0 && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-2 dark:border-emerald-900 dark:bg-emerald-950/10">
          <p className="mb-1 text-[11px] font-semibold text-emerald-800 dark:text-emerald-300">Case export packs {packLoading ? '(refreshing...)' : ''}</p>
          <div className="space-y-1.5 max-h-28 overflow-y-auto pr-1">
            {exportPacks.map(pack => (
              <div key={pack.id} className="flex items-center justify-between gap-2 rounded border border-emerald-200 bg-background/80 px-2 py-1.5 dark:border-emerald-900">
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-semibold text-foreground" title={pack.originalFilename}>{pack.title || pack.originalFilename}</p>
                  <p className="text-[10px] text-muted-foreground">{pack.attachmentCount} item(s) · {formatBytes(pack.fileSizeBytes)} · created {formatWhen(pack.createdAtUtc)} by {packActorName(pack)}{pack.customerVisible ? ' · customer-visible' : ''}</p>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {canDownloadPack && <button type="button" onClick={() => downloadPack(pack)} className="text-[10px] rounded border border-border px-2 py-1 hover:bg-muted">Download pack</button>}
                  {canCustomerVisible && <button type="button" onClick={() => void setPackCustomerVisible(pack, !pack.customerVisible)} className="text-[10px] rounded border border-cyan-300 text-cyan-700 dark:text-cyan-300 px-2 py-1 hover:bg-cyan-50 dark:hover:bg-cyan-950/30">{pack.customerVisible ? 'Make internal' : 'Customer-visible'}</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {attachments.length === 0 && !loading && <p className="text-xs text-muted-foreground italic py-2">No evidence attachments uploaded yet.</p>}
        {attachments.map(row => (
          <div key={row.id} className={`rounded-lg border p-2 ${row.isDeleted ? 'border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20' : 'border-border bg-muted/30'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-xs font-semibold text-foreground truncate" title={row.originalFilename}>{row.originalFilename}</p>
                  <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-300">{row.category || 'Other'}</span>
                  {canViewScan && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${scanClass(row.scanStatus)}`}>Scan: {scanLabel(row.scanStatus)}</span>}
                  {row.customerVisible && <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-300">Customer-visible</span>}
                  {row.isLocked && <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">Locked</span>}
                  {row.legalHold && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">Legal hold</span>}
                  {row.isDeleted && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">Deleted</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{formatBytes(row.fileSizeBytes)} · uploaded {formatWhen(row.uploadedAtUtc)} by {actorName(row)}</p>
                {row.uploadedByName && row.uploadedByEmail && <p className="text-[11px] text-muted-foreground">Uploader email: {row.uploadedByEmail}</p>}
                {row.description && <p className="text-[11px] text-muted-foreground mt-1"><span className="font-semibold">Description:</span> {row.description}</p>}
                {canViewScan && row.scanResult && <p className="text-[11px] text-muted-foreground mt-1"><span className="font-semibold">Scan result:</span> {row.scanResult}{row.scannedAtUtc ? ` · checked ${formatWhen(row.scannedAtUtc)}` : ''}</p>}
                {row.retentionUntilUtc && <p className="text-[11px] text-muted-foreground mt-1"><span className="font-semibold">Retention until:</span> {formatWhen(row.retentionUntilUtc)}</p>}
                {row.isLocked && <p className="text-[11px] text-muted-foreground mt-1"><span className="font-semibold">Locked:</span> {formatWhen(row.lockedAtUtc)}{row.lockedBy ? ` by ${row.lockedBy}` : ''}{row.lockReason ? ` · ${row.lockReason}` : ''}</p>}
                {row.legalHold && <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1"><span className="font-semibold">Legal hold:</span> {formatWhen(row.legalHoldAtUtc)}{row.legalHoldBy ? ` by ${row.legalHoldBy}` : ''}{row.legalHoldReason ? ` · ${row.legalHoldReason}` : ''}</p>}
                {row.isDeleted && <p className="text-[11px] text-red-700 dark:text-red-300 mt-1">Soft-deleted {formatWhen(row.deletedAtUtc)}{row.deletedBy ? ` by ${row.deletedBy}` : ''}</p>}
                {canViewHash && row.sha256Hash && <p className="text-[11px] font-mono text-muted-foreground mt-1" title={row.sha256Hash}>SHA256: {compactHash(row.sha256Hash)}</p>}
              </div>
              <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[290px]">
                {canDownload && !row.isDeleted && <button type="button" onClick={() => download(row)} className="text-[11px] rounded border border-border px-2 py-1 hover:bg-background">Download</button>}
                {canViewScan && !row.isDeleted && <button type="button" onClick={() => void refreshScan(row)} className="text-[11px] rounded border border-blue-200 text-blue-700 dark:text-blue-300 px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-950/30">Refresh scan</button>}
                {canCustomerVisible && !row.isDeleted && <button type="button" onClick={() => void setCustomerVisible(row, !row.customerVisible)} className="text-[11px] rounded border border-violet-300 text-violet-700 dark:text-violet-300 px-2 py-1 hover:bg-violet-50 dark:hover:bg-violet-950/30">{row.customerVisible ? 'Make internal' : 'Customer-visible'}</button>}
                {canLock && !row.isDeleted && !row.isLocked && <button type="button" onClick={() => void lockAttachment(row)} className="text-[11px] rounded border border-slate-300 text-slate-700 dark:text-slate-300 px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-950/30">Lock</button>}
                {canUnlock && !row.isDeleted && row.isLocked && !row.legalHold && <button type="button" onClick={() => void unlockAttachment(row)} className="text-[11px] rounded border border-slate-300 text-slate-700 dark:text-slate-300 px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-950/30">Unlock</button>}
                {canLegalHold && !row.isDeleted && <button type="button" onClick={() => void setLegalHold(row, !row.legalHold)} className="text-[11px] rounded border border-amber-300 text-amber-700 dark:text-amber-300 px-2 py-1 hover:bg-amber-50 dark:hover:bg-amber-950/30">{row.legalHold ? 'Remove hold' : 'Legal hold'}</button>}
                {canViewAudit && <button type="button" onClick={() => void toggleAudit(row)} className="text-[11px] rounded border border-border px-2 py-1 hover:bg-background">{auditOpen[row.id] ? 'Hide timeline' : 'Timeline'}</button>}
                {canDelete && !row.isDeleted && !row.isLocked && !row.legalHold && <button type="button" onClick={() => void deleteAttachment(row)} className="text-[11px] rounded border border-red-300 text-red-700 dark:text-red-300 px-2 py-1 hover:bg-red-50 dark:hover:bg-red-950/30">Delete</button>}
                {canRestore && row.isDeleted && <button type="button" onClick={() => void restoreAttachment(row)} className="text-[11px] rounded border border-emerald-300 text-emerald-700 dark:text-emerald-300 px-2 py-1 hover:bg-emerald-50 dark:hover:bg-emerald-950/30">Restore</button>}
              </div>
            </div>
            {auditOpen[row.id] && (
              <div className="mt-2 rounded-lg border border-border bg-background/80 p-2">
                <p className="mb-1 text-[11px] font-semibold text-muted-foreground">Audit timeline</p>
                {auditLoading[row.id] && <p className="text-[11px] text-muted-foreground">Loading timeline…</p>}
                {!auditLoading[row.id] && (auditRows[row.id]?.length ?? 0) === 0 && <p className="text-[11px] text-muted-foreground">No timeline entries available yet.</p>}
                {(auditRows[row.id] ?? []).map(ev => (
                  <div key={ev.id} className="border-l-2 border-cyan-300 pl-2 py-1 text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{ev.action.replace(/_/g, ' ')}</span> · {formatWhen(ev.createdAtUtc)} · {ev.actorName || ev.actorEmail || 'system'}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
